import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { COLORS, GAME } from '../constants';
import type { PlayerId } from '../systems/InputRouter';
import type { BiomeType } from '../systems/LevelLoader';

// Player colors for highlights
const HIGHLIGHT_COLORS: Record<PlayerId, number> = {
  'P1': 0x4444ff, // Blue
  'P2': 0xff4444, // Red
  'P3': 0xffff44, // Yellow
  'P4': 0x44ff44, // Green
};

// ============================================
// TreeSlot Entity (Buildable location)
// ============================================

export interface TreeSlotConfig extends Omit<EntityConfig, 'type'> {
  slotId?: string;
  biome?: BiomeType;
}

export class TreeSlot extends Entity {
  public readonly slotId: string;
  private occupied: boolean = false;
  private towerId: string | null = null;

  // Visuals
  private circle: Phaser.GameObjects.Arc;
  private tree: Phaser.GameObjects.Image | null = null;
  private shadow: Phaser.GameObjects.Container | null = null;
  private highlights: Map<PlayerId, Phaser.GameObjects.Arc> = new Map();
  private treeVariant: number; // Which tree image to use (1-9 forest, 1-8 bog, 1-10 ruins)
  private treeKeyPrefix: string; // 'tree-', 'tree-bog-', or 'tree-ruins-'
  private windTween: Phaser.Tweens.Tween | null = null;
  private currentBiome: BiomeType; // Used for wind strength (ruins = 3x gusty)

  // Events
  public onSelected?: (slot: TreeSlot) => void;

  constructor(scene: Phaser.Scene, config: TreeSlotConfig) {
    super(scene, { ...config, type: 'treeSlot' });

    this.slotId = config.slotId || config.id;

    const biome = config.biome ?? 'forest';
    this.currentBiome = biome;
    const maxVariant = biome === 'bog' ? 8 : biome === 'ruins' ? 10 : biome === 'volcano' ? 12 : 9;
    this.treeVariant = Math.floor(Math.random() * maxVariant) + 1;
    this.treeKeyPrefix = biome === 'bog' ? 'tree-bog-' : biome === 'ruins' ? 'tree-ruins-' : biome === 'volcano' ? 'tree-volcano-' : 'tree-';

    // Create invisible hitbox for interaction
    this.circle = scene.add.circle(0, 0, GAME.TREE_SLOT_RADIUS, COLORS.TREE_SLOT, 0);
    this.circle.setStrokeStyle(0);

    // Tree shadow - fuzzy shadow using multiple layered ellipses (volcano overridden in applyShadowForBiome when setBiome is used)
    this.shadow = scene.add.container(0, 18);
    const shadowLayers = [
      { width: 70, height: 24, alpha: 0.08 },
      { width: 60, height: 20, alpha: 0.12 },
      { width: 50, height: 16, alpha: 0.15 },
      { width: 40, height: 12, alpha: 0.2 },
    ];
    shadowLayers.forEach((layer) => {
      const ellipse = scene.add.ellipse(0, 0, layer.width, layer.height, 0x000000, layer.alpha);
      this.shadow!.add(ellipse);
    });
    if (biome === 'volcano') {
      this.applyShadowForBiome('volcano');
    }

    // Tree visual - use actual tree image (forest s1, bog b1, ruins r1, volcano v1)
    const treeKey = `${this.treeKeyPrefix}${this.treeVariant}`;
    if (scene.textures.exists(treeKey)) {
      this.tree = scene.add.image(0, 20, treeKey);
      // Scale tree to fit nicely in the slot
      this.tree.setScale(0.3);
      this.tree.setOrigin(0.5, 1); // Anchor at bottom center (enables sway-from-base wind effect)
      // Wind sway: forest/bog/ruins; volcano = no wind (still volcanic landscape)
      if (this.currentBiome !== 'volcano') {
        this.windTween = this.createWindTween();
      }
    } else {
      // Fallback to triangle if image not loaded
      const triangle = scene.add.triangle(0, -5, 0, -15, -10, 10, 10, 10, 0x006400);
      this.tree = triangle as unknown as Phaser.GameObjects.Image;
    }

    // Add shadow first so it renders behind the tree
    this.container.add([this.circle, this.shadow, this.tree]);

    // Make interactive
    this.circle.setInteractive({ useHandCursor: true });
    this.circle.on('pointerover', () => this.onPointerOver());
    this.circle.on('pointerout', () => this.onPointerOut());
    this.circle.on('pointerdown', () => this.onPointerDown());

    // Y-based depth for 2.5D sorting (bottom of tree - lower on screen = in front)
    this.updateDepth();
  }

  /** Wind params: ruins = 2x sway + faster cycle (gusty). */
  private getWindParams(): { swayDeg: number; swayDuration: number } {
    const baseSway = 1.8;
    const baseDuration = 2500 + Math.random() * 1500; // 2.5–4s per half-cycle
    if (this.currentBiome === 'ruins') {
      return { swayDeg: baseSway * 2, swayDuration: baseDuration / 2 };
    }
    return { swayDeg: baseSway, swayDuration: baseDuration };
  }

  private createWindTween(): Phaser.Tweens.Tween {
    const { swayDeg, swayDuration } = this.getWindParams();
    const startDelay = Math.random() * 2000;
    if (this.tree) this.tree.angle = -swayDeg;
    return this.scene.tweens.add({
      targets: this.tree,
      angle: { from: -swayDeg, to: swayDeg },
      duration: swayDuration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: startDelay,
    });
  }

  /** Volcano: shadow higher (y=12) and less opaque; other biomes: y=18, default alphas. */
  private applyShadowForBiome(biome: BiomeType): void {
    if (!this.shadow) return;
    this.shadow.y = biome === 'volcano' ? 12 : 18;
    const alphas = biome === 'volcano' ? [0.04, 0.06, 0.075, 0.1] : [0.08, 0.12, 0.15, 0.2];
    this.shadow.list.forEach((obj, i) => {
      const ellipse = obj as Phaser.GameObjects.Ellipse;
      if (ellipse.setFillStyle && alphas[i] !== undefined) ellipse.setFillStyle(0x000000, alphas[i]);
    });
  }

  /** Switch biome (forest/bog/ruins/volcano) and update tree texture + wind + shadow. Used in experiment mode. */
  setBiome(biome: BiomeType): void {
    this.currentBiome = biome;
    this.treeKeyPrefix = biome === 'bog' ? 'tree-bog-' : biome === 'ruins' ? 'tree-ruins-' : biome === 'volcano' ? 'tree-volcano-' : 'tree-';
    const maxVariant = biome === 'bog' ? 8 : biome === 'ruins' ? 10 : biome === 'volcano' ? 12 : 9;
    this.treeVariant = Math.min(this.treeVariant, maxVariant);
    const newKey = `${this.treeKeyPrefix}${this.treeVariant}`;
    if (this.tree && !this.occupied && this.scene.textures.exists(newKey)) {
      this.tree.setTexture(newKey);
    }
    this.applyShadowForBiome(biome);
    // Restart wind tween (ruins = 2x gusty); volcano = no wind
    if (this.windTween) {
      this.windTween.stop();
      this.windTween = null;
    }
    if (this.tree && !this.occupied && biome !== 'volcano') {
      this.windTween = this.createWindTween();
    }
  }

  /** Bottom of tree visual for depth sorting (tree at local y=20 with origin bottom) */
  protected getDepthY(): number {
    return this._y + 20;
  }

  update(_delta: number): void {
    // TreeSlots don't need per-frame updates (static position)
  }

  private onPointerOver(): void {
    if (this.occupied) return;
    this.showHighlight(true, 'P1');
  }

  private onPointerOut(): void {
    this.showHighlight(false, 'P1');
  }

  private onPointerDown(): void {
    if (!this.occupied) {
      this.onSelected?.(this);
    }
  }

  /**
   * Mark slot as occupied by a tower
   */
  setOccupied(towerId: string): void {
    this.occupied = true;
    this.towerId = towerId;

    // Hide tree and shadow, pause wind tween
    if (this.tree) {
      this.tree.setVisible(false);
    }
    if (this.windTween) {
      this.windTween.pause();
    }
    if (this.shadow) {
      this.shadow.setVisible(false);
    }
    // Make circle fully transparent when occupied (no grey blob behind tower)
    this.circle.setFillStyle(0x000000, 0);
    this.circle.setStrokeStyle(0);
    this.circle.disableInteractive();
  }

  /**
   * Clear occupation (if tower is sold/destroyed)
   */
  clearOccupied(): void {
    this.occupied = false;
    this.towerId = null;

    // Show tree and shadow again, resume wind tween
    if (this.tree) {
      this.tree.setVisible(true);
    }
    if (this.windTween) {
      this.windTween.resume();
    }
    if (this.shadow) {
      this.shadow.setVisible(true);
    }
    // Restore circle to normal idle state (invisible hitbox, no green ring)
    this.circle.setFillStyle(COLORS.TREE_SLOT, 0);
    this.circle.setStrokeStyle(0);
    this.circle.setInteractive({ useHandCursor: true });
  }

  /**
   * Show/hide selection highlight per player (supports up to 4 players)
   */
  showHighlight(show: boolean, playerId: PlayerId = 'P1'): void {
    const current = this.highlights.get(playerId);
    if (show && !current && !this.occupied) {
      const color = HIGHLIGHT_COLORS[playerId] || COLORS.PLAYER_1;
      const ring = this.scene.add.circle(0, 0, GAME.TREE_SLOT_RADIUS + 5);
      ring.setStrokeStyle(3, color);
      ring.setFillStyle(color, 0.2);
      this.container.add(ring);
      this.container.sendToBack(ring);
      this.highlights.set(playerId, ring);
    } else if (!show && current) {
      current.destroy();
      this.highlights.delete(playerId);
    }
  }

  // Getters
  isOccupied(): boolean { return this.occupied; }
  getTowerId(): string | null { return this.towerId; }

  destroy(): void {
    if (this.windTween) {
      this.windTween.stop();
      this.windTween = null;
    }
    super.destroy();
  }

  /**
   * Check if a point is within this slot's area
   */
  containsPoint(x: number, y: number): boolean {
    const dx = x - this._x;
    const dy = y - this._y;
    return dx * dx + dy * dy <= GAME.TREE_SLOT_RADIUS * GAME.TREE_SLOT_RADIUS;
  }
}
